import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Modal,
  Stack,
  Select,
  Switch,
  Button,
  Text,
  Group,
  Badge,
} from "@mantine/core";
import {
  IconSparkles,
  IconArrowRight,
  IconPlus,
  IconPlayerPlay,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useLocation } from "wouter";
import {
  quickCreateMappingProfile,
  getMappingProfiles,
  executeMappingProfile,
} from "../api";
import { getPimSchemas } from "../Pim/pimApi";
import { useStore } from "../store";

export default function MapToTargetDialog({ opened, onClose, importData }) {
  const { t } = useTranslation("imports");
  const userProfile = useStore((state) => state.profile);
  const [, navigate] = useLocation();
  const [schemas, setSchemas] = useState([]);
  const [existingProfiles, setExistingProfiles] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [applyAiSuggestions, setApplyAiSuggestions] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [loading, setLoading] = useState(false);

  // Parse selected option to determine if it's an existing profile or new schema
  const isExistingProfile = selectedOption?.startsWith("profile:");
  const selectedProfileId = isExistingProfile
    ? selectedOption.replace("profile:", "")
    : null;
  const selectedSchemaId =
    !isExistingProfile && selectedOption?.startsWith("schema:")
      ? selectedOption.replace("schema:", "")
      : null;

  // Load target schemas and existing mapping profiles
  useEffect(() => {
    if (opened && userProfile?.tenantId) {
      setLoading(true);
      setSelectedOption(null);

      Promise.all([getPimSchemas(userProfile.tenantId), getMappingProfiles()])
        .then(([schemasData, profilesData]) => {
          setSchemas(schemasData || []);

          // Filter profiles that match this import's source schema
          const compatibleProfiles = (profilesData || []).filter(
            (p) => p.sourceSchemaId === importData?.schemaId
          );
          setExistingProfiles(compatibleProfiles);
        })
        .catch(() => {
          notifications.show({
            color: "red",
            title: t("mapToTarget.loadFailedTitle"),
            message: t("mapToTarget.loadFailedMessage"),
          });
        })
        .finally(() => setLoading(false));
    }
  }, [opened, userProfile?.tenantId, importData?.schemaId, t]);

  // Build grouped options for the select
  const buildSelectOptions = () => {
    const groups = [];

    // Existing profiles group
    if (existingProfiles.length > 0) {
      const getSchemaName = (schemaId) => {
        const schema = schemas.find((s) => s.id === schemaId);
        return schema?.labels?.en || schema?.name || t("mapToTarget.unknownSchema");
      };

      groups.push({
        group: t("mapToTarget.groupExisting"),
        items: existingProfiles.map((profile) => ({
          value: `profile:${profile.id}`,
          label: t("mapToTarget.profileRules", {
            name: profile.name,
            count: profile.ruleCount,
          }),
          description: t("mapToTarget.profileTarget", {
            schema: getSchemaName(profile.targetSchemaId),
          }),
        })),
      });
    }

    // New mapping group - exclude schemas that already have a profile
    const schemasWithProfiles = new Set(
      existingProfiles.map((p) => p.targetSchemaId)
    );
    const availableSchemas = schemas.filter(
      (s) => !schemasWithProfiles.has(s.id) && s.id !== importData?.schemaId
    );

    if (availableSchemas.length > 0) {
      groups.push({
        group: t("mapToTarget.groupNew"),
        items: availableSchemas.map((schema) => ({
          value: `schema:${schema.id}`,
          label: schema.labels?.en || schema.name,
        })),
      });
    }

    return groups;
  };

  const handleExecuteMapping = async () => {
    try {
      setIsExecuting(true);
      const result = await executeMappingProfile(selectedProfileId, {
        importId: importData.id,
        createProducts: true,
      });

      notifications.show({
        color: "green",
        title: t("mapToTarget.executedTitle"),
        message: t("mapToTarget.executedMessage", {
          rows: result.totalRows,
          succeeded: result.successCount,
          failed: result.errorCount,
        }),
      });

      onClose();
      navigate(
        `/imports/${importData.id}/mappings/${result.mappingProcessId}`
      );
    } catch (error) {
      notifications.show({
        color: "red",
        title: t("mapToTarget.executeFailedTitle"),
        message: error.message || t("mapToTarget.executeFailedMessage"),
      });
    } finally {
      setIsExecuting(false);
    }
  };

  const handleOpenProfile = () => {
    onClose();
    navigate(`/mapping-profiles/${selectedProfileId}`);
  };

  const handleCreateProfile = async () => {
    try {
      setIsCreating(true);
      const result = await quickCreateMappingProfile(
        importData.id,
        selectedSchemaId,
        applyAiSuggestions
      );

      notifications.show({
        color: "green",
        title: t("mapToTarget.createdTitle"),
        message:
          result.aiSuggestionsApplied > 0
            ? t("mapToTarget.createdWithAi", { count: result.aiSuggestionsApplied })
            : t("mapToTarget.createdPlain"),
      });

      onClose();
      navigate(`/mapping-profiles/${result.profileId}`);
    } catch (error) {
      notifications.show({
        color: "red",
        title: t("mapToTarget.createFailedTitle"),
        message: error.message || t("mapToTarget.createFailedMessage"),
      });
    } finally {
      setIsCreating(false);
    }
  };

  const selectOptions = buildSelectOptions();

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconArrowRight size={20} />
          <Text fw={500}>{t("mapToTarget.title")}</Text>
        </Group>
      }
      centered
    >
      <Stack>
        <Text size="sm" c="dimmed">
          {existingProfiles.length > 0
            ? t("mapToTarget.introExisting")
            : t("mapToTarget.introNew")}
        </Text>

        <Select
          label={t("mapToTarget.target")}
          placeholder={loading ? t("mapToTarget.loading") : t("mapToTarget.selectPlaceholder")}
          data={selectOptions}
          value={selectedOption}
          onChange={setSelectedOption}
          searchable
          disabled={loading || isCreating}
          renderOption={({ option }) => (
            <Group gap="xs" justify="space-between" wrap="nowrap">
              <Text size="sm" truncate>
                {option.label}
              </Text>
              {option.value.startsWith("profile:") && (
                <Badge size="xs" variant="light" color="blue">
                  {t("mapToTarget.existingBadge")}
                </Badge>
              )}
            </Group>
          )}
        />

        {!isExistingProfile && selectedSchemaId && (
          <>
            <Switch
              label={t("mapToTarget.applyAi")}
              description={t("mapToTarget.applyAiHint")}
              checked={applyAiSuggestions}
              onChange={(e) => setApplyAiSuggestions(e.currentTarget.checked)}
              disabled={isCreating}
            />

            {applyAiSuggestions && (
              <Badge
                size="sm"
                variant="light"
                color="violet"
                leftSection={<IconSparkles size={12} />}
              >
                {t("mapToTarget.aiBadge")}
              </Badge>
            )}
          </>
        )}

        <Group justify="flex-end" mt="md">
          <Button
            variant="subtle"
            onClick={onClose}
            disabled={isCreating || isExecuting}
          >
            {t("mapToTarget.cancel")}
          </Button>
          {isExistingProfile ? (
            <>
              <Button
                variant="default"
                leftSection={<IconArrowRight size={16} />}
                onClick={handleOpenProfile}
                disabled={isExecuting}
              >
                {t("mapToTarget.openProfile")}
              </Button>
              <Button
                leftSection={<IconPlayerPlay size={16} />}
                loading={isExecuting}
                onClick={handleExecuteMapping}
              >
                {t("mapToTarget.executeMapping")}
              </Button>
            </>
          ) : (
            <Button
              loading={isCreating}
              disabled={!selectedOption}
              leftSection={<IconPlus size={16} />}
              onClick={handleCreateProfile}
            >
              {t("mapToTarget.createOpen")}
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}
